/*!
============================================================
Core Media Indonesia Shipping Engine
Version : 2.0.0
Author  : Core Media Indonesia
============================================================
*/

(function (window, document) {

'use strict';

/* ==========================================================
   SHIPPING ENGINE
========================================================== */

class ShippingEngine {

    constructor(options = {}) {

        /* -----------------------------------------
           CONFIG
        ----------------------------------------- */

        this.config = {

            dataUrl: "/assets/shipping-rates.json",

            storageKey: "cmi_shipping_city",

            debounce: 250,

            searchLimit: 8,

            volumetricDivisor: 6000,

            cutoffHour: 15,

            cache: true,

            debug: false,

            ...options

        };

        /* -----------------------------------------
           DATA
        ----------------------------------------- */

        this.data = {};

        this.cityIndex = [];

        this.codeIndex = new Map();

        this.searchCache = new Map();

        this.alias = new Map();

        this.eventPool = [];

        this.dom = {};

        this.ready = false;

        this.version = "2.0.0";

        this.initAlias();

    }

    /* =====================================================
       INITIALIZE
    ===================================================== */

    async init() {

        await this.loadRates();

        this.buildIndex();

        this.ready = true;

        return this;

    }

    /* =====================================================
       DEBUG
    ===================================================== */

    log(...msg){

        if(this.config.debug){

            console.log("[ShippingEngine]",...msg);

        }

    }
    /* =====================================================
       NORMALIZER
    ===================================================== */

    normalize(text){

        if(text===undefined || text===null){

            return "";

        }

        return text
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g,"")
            .replace(/[^\w\s]/g," ")
            .replace(/\s+/g," ")
            .trim();

    }

    /* =====================================================
       ALIAS
    ===================================================== */

    initAlias(){

        const alias={

            bdg:"bandung",
            jkt:"jakarta",
            jogja:"yogyakarta",
            yk:"yogyakarta",
            sby:"surabaya",
            smg:"semarang",
            mlg:"malang",
            dpk:"depok",
            bks:"bekasi",
            tgr:"tangerang",

            jaktim:"jakarta timur",
            jakbar:"jakarta barat",
            jakpus:"jakarta pusat",
            jaksel:"jakarta selatan",
            jakut:"jakarta utara"

        };

        Object.keys(alias).forEach(key=>{

            this.alias.set(key,alias[key]);

        });

    }

    resolveAlias(keyword){

        keyword=this.normalize(keyword);

        return this.alias.get(keyword) || keyword;

    }

    /* =====================================================
       DEBOUNCE
    ===================================================== */

    debounce(fn,delay=this.config.debounce){

        let timer;

        return (...args)=>{

            clearTimeout(timer);

            timer=setTimeout(()=>{

                fn.apply(this,args);

            },delay);

        };

    }

    /* =====================================================
       FORMATTER
    ===================================================== */

    formatCurrency(value){

        return new Intl.NumberFormat("id-ID",{

            style:"currency",

            currency:"IDR",

            maximumFractionDigits:0

        }).format(value);

    }

    formatWeight(weight){

        return Number(weight).toFixed(2)+" kg";

    }

    /* =====================================================
       UUID
    ===================================================== */

    uid(){

        return Math.random()

            .toString(36)

            .substring(2,10);

    }

    /* =====================================================
       EMPTY
    ===================================================== */

    isEmpty(value){

        return value===undefined ||

               value===null ||

               value==="";

    }
    /* =====================================================
       LOAD SHIPPING RATES
    ===================================================== */

    async loadRates(url = this.config.dataUrl) {

        this.log("Loading shipping rates:", url);

        const response = await fetch(url, {
            cache: "no-cache"
        });

        if (!response.ok) {
            throw new Error(
                `Gagal memuat shipping-rates.json (${response.status})`
            );
        }

        const json = await response.json();

        this.validateRates(json);

        this.data = json;

        this.log("Rates loaded");

        return this.data;

    }

    /* =====================================================
       VALIDATE JSON
    ===================================================== */

    validateRates(data) {

        if (typeof data !== "object" || data === null) {
            throw new Error("shipping-rates.json bukan object.");
        }

        const total = Object.keys(data).length;

        if (!total) {
            throw new Error("shipping-rates.json kosong.");
        }

        this.log(`${total} kota ditemukan.`);

        return true;

    }

    /* =====================================================
       BUILD INDEX
    ===================================================== */

    buildIndex() {

        this.cityIndex.length = 0;

        this.codeIndex.clear();

        this.provinceIndex = new Map();

        this.hubIndex = new Map();

        this.zoneIndex = new Map();

        Object.entries(this.data).forEach(([code, city]) => {

            const item = {

                code,

                city: city.city,

                province: city.province,

                hub: city.hub,

                zone: city.zone,

                grab: city.grab,

                eta: city.eta,

                estimated_days: city.estimated_days,

                free_shipping: city.free_shipping,

                search: this.normalize(city.city)

            };

            this.cityIndex.push(item);

            this.codeIndex.set(code, city);

            /* province index */

            if (!this.provinceIndex.has(item.province)) {

                this.provinceIndex.set(item.province, []);

            }

            this.provinceIndex.get(item.province).push(item);

            /* hub index */

            if (!this.hubIndex.has(item.hub)) {

                this.hubIndex.set(item.hub, []);

            }

            this.hubIndex.get(item.hub).push(item);

            /* zone index */

            if (!this.zoneIndex.has(item.zone)) {

                this.zoneIndex.set(item.zone, []);

            }

            this.zoneIndex.get(item.zone).push(item);

        });

        this.log(
            `${this.cityIndex.length} city index berhasil dibuat.`
        );

    }

    /* =====================================================
       BASIC GETTERS
    ===================================================== */

    getCity(code) {

        return this.codeIndex.get(code) || null;

    }

    hasCity(code) {

        return this.codeIndex.has(code);

    }

    getCities() {

        return this.cityIndex;

    }

    getProvinces() {

        return Array.from(this.provinceIndex.keys());

    }

    getProvinceCities(province) {

        return this.provinceIndex.get(province) || [];

    }

    getHubCities(hub) {

        return this.hubIndex.get(hub) || [];

    }

    getZoneCities(zone) {

        return this.zoneIndex.get(zone) || [];

    }

    getCityCount() {

        return this.cityIndex.length;

    }

    /* =====================================================
       STORAGE
    ===================================================== */

    saveCity(code) {

        if (!this.hasCity(code)) {

            return false;

        }

        localStorage.setItem(

            this.config.storageKey,

            code

        );

        return true;

    }

    loadCityCode() {

        return localStorage.getItem(

            this.config.storageKey

        );

    }

    loadCity() {

        const code = this.loadCityCode();

        if (!code) {

            return null;

        }

        return this.getCity(code);

    }

    clearCity() {

        localStorage.removeItem(

            this.config.storageKey

        );

    }
    /* =====================================================
       SEARCH ENGINE
    ===================================================== */

    search(keyword, limit = this.config.searchLimit) {

        keyword = this.resolveAlias(keyword);

        if (this.isEmpty(keyword)) {

            return this.cityIndex.slice(0, limit);

        }

        /* ---------- CACHE ---------- */

        if (this.config.cache && this.searchCache.has(keyword)) {

            return this.searchCache.get(keyword);

        }

        const tokens = keyword.split(" ").filter(Boolean);

        const result = [];

        for (const city of this.cityIndex) {

            const score = this.calculateScore(city.search, tokens);

            if (score > 0) {

                result.push({

                    ...city,

                    score

                });

            }

        }

        result.sort((a, b) => {

            if (b.score !== a.score) {

                return b.score - a.score;

            }

            return a.city.localeCompare(b.city);

        });

        const output = result.slice(0, limit);

        if (this.config.cache) {

            this.searchCache.set(keyword, output);

        }

        return output;

    }

    /* =====================================================
       SEARCH SCORE
    ===================================================== */

    calculateScore(text, tokens) {

        let score = 0;

        for (const token of tokens) {

            if (!token.length) {

                continue;

            }

            if (text === token) {

                score += 100;

                continue;

            }

            if (text.startsWith(token)) {

                score += 60;

            }

            if (text.includes(" " + token)) {

                score += 35;

            }

            if (text.includes(token)) {

                score += 20;

            }

            score += this.partialScore(text, token);

        }

        return score;

    }

    /* =====================================================
       PARTIAL SCORE
    ===================================================== */

    partialScore(text, token) {

        let score = 0;

        const words = text.split(" ");

        for (const word of words) {

            if (word.startsWith(token)) {

                score += 10;

            }

            if (word.includes(token)) {

                score += 5;

            }

        }

        return score;

    }

    /* =====================================================
       GET CITY BY NAME
    ===================================================== */

    getCityByName(name) {

        const result = this.search(name, 1);

        if (!result.length) {

            return null;

        }

        return this.getCity(result[0].code);

    }

    /* =====================================================
       CLEAR SEARCH CACHE
    ===================================================== */

    clearSearchCache() {

        this.searchCache.clear();

    }

    /* =====================================================
       HIGHLIGHT KEYWORD
    ===================================================== */

    highlight(text, keyword) {

        if (this.isEmpty(keyword)) {

            return text;

        }

        const escaped = keyword.replace(

            /[.*+?^${}()|[\]\\]/g,

            "\\$&"

        );

        const regex = new RegExp("(" + escaped + ")", "ig");

        return text.replace(regex, "<mark>$1</mark>");

    }

    /* =====================================================
       SEARCH STATS
    ===================================================== */

    getSearchInfo() {

        return {

            cities: this.cityIndex.length,

            cache: this.searchCache.size,

            version: this.version

        };

    }
    /* =====================================================
       WEIGHT ENGINE
    ===================================================== */

    calculateVolumetricWeight(
        length,
        width,
        height,
        divisor = this.config.volumetricDivisor
    ) {

        length = Number(length) || 0;
        width = Number(width) || 0;
        height = Number(height) || 0;

        if (length <= 0 || width <= 0 || height <= 0) {

            return 0;

        }

        return (length * width * height) / divisor;

    }

    /* =====================================================
       ROUNDING
    ===================================================== */

    roundWeight(weight, mode = "ceil") {

        weight = Number(weight) || 0;

        switch (mode) {

            case "round":
                return Math.round(weight);

            case "decimal":
                return Number(weight.toFixed(2));

            case "ceil":
            default:
                return Math.ceil(weight);

        }

    }

    /* =====================================================
       CHARGEABLE WEIGHT
    ===================================================== */

    calculateChargeableWeight({

        actualWeight = 0,

        length = 0,

        width = 0,

        height = 0,

        rounding = "ceil"

    } = {}) {

        actualWeight = Number(actualWeight) || 0;

        const volumetric = this.calculateVolumetricWeight(

            length,

            width,

            height

        );

        const chargeable = Math.max(

            actualWeight,

            volumetric

        );

        return {

            actual: Number(actualWeight.toFixed(2)),

            volumetric: Number(volumetric.toFixed(2)),

            chargeable: this.roundWeight(

                chargeable,

                rounding

            )

        };

    }

    /* =====================================================
       WEIGHT TIER
    ===================================================== */

    getWeightTier(weight) {

        weight = this.roundWeight(weight);

        const tiers = [

            1,

            3,

            5,

            10,

            20,

            30,

            50

        ];

        for (const tier of tiers) {

            if (weight <= tier) {

                return tier;

            }

        }

        return null;

    }

    /* =====================================================
       RATE MODE
    ===================================================== */

    getRate(city, weight) {
 
        if (!city) {
 
            return null;
 
        }
 
        /*
        =====================================
        MODE 1
        tier pricing (hanya kalau city.rates ada)
        =====================================
        */
 
        if (city.rates) {
 
            const tier = this.getWeightTier(weight);
 
            if (tier !== null && city.rates[tier] !== undefined) {
 
                return {
 
                    mode: "tier",
 
                    tier,
 
                    price: city.rates[tier]
 
                };
 
            }
 
        }
 
        /*
        =====================================
        MODE 2
        per kg
        =====================================
        */
 
        if (city.price_per_kg) {
 
            return {
 
                mode: "perkg",
 
                tier: weight,
 
                price:
 
                    Math.ceil(weight)
 
                    *
 
                    city.price_per_kg
 
            };
 
        }
 
        return null;
 
    }

    /* =====================================================
       MAX WEIGHT
    ===================================================== */

    validateWeight(weight) {

        if (weight <= 0) {

            return {

                valid: false,

                message: "Berat harus lebih dari 0 kg."

            };

        }

        if (weight > 100) {

            return {

                valid: false,

                message: "Berat melebihi batas maksimal."

            };

        }

        return {

            valid: true,

            message: null

        };

    }

    /* =====================================================
       SHIPPING WEIGHT SUMMARY
    ===================================================== */

    buildWeightSummary(options = {}) {

        const result = this.calculateChargeableWeight(options);

        return {

            ...result,

            actualLabel: this.formatWeight(result.actual),

            volumetricLabel: this.formatWeight(result.volumetric),

            chargeableLabel: this.formatWeight(result.chargeable)

        };

    }
    /* =====================================================
       DISPATCH STATUS (cutoff jam kirim)
    ===================================================== */

    getDispatchStatus() {

        const now = new Date();
        const hour = now.getHours();
        const cutoff = this.config.cutoffHour;

        if (hour < cutoff) {
            return {
                status: "today",
                cutoffHour: cutoff,
                message: `Pesan sebelum jam ${cutoff}:00, diproses & dikirim hari ini`
            };
        }

        return {
            status: "next_day",
            cutoffHour: cutoff,
            message: `Sudah lewat jam ${cutoff}:00, pesanan diproses besok`
        };

    }

    /* =====================================================
       SHIPPING CALCULATOR
    ===================================================== */

    calculateShipping({

        cityCode,

        actualWeight,

        length = 0,

        width = 0,

        height = 0,

        rounding = "ceil"

    } = {}) {

        if (!this.hasCity(cityCode)) {

            return {

                success: false,

                error: "CITY_NOT_FOUND",

                message: "Kota tujuan tidak ditemukan."

            };

        }

        const city = this.getCity(cityCode);

        const weight = this.calculateChargeableWeight({

            actualWeight,

            length,

            width,

            height,

            rounding

        });

        const validation = this.validateWeight(

            weight.chargeable

        );

        if (!validation.valid) {

            return {

                success: false,

                error: "INVALID_WEIGHT",

                message: validation.message

            };

        }

        const rate = this.getRate(

            city,

            weight.chargeable

        );

        if (!rate) {

            return {

                success: false,

                error: "RATE_NOT_FOUND",

                message: "Tarif tidak tersedia."

            };

        }

        const subtotal = Number(rate.price);

        const shipping = {

            success: true,

            version: this.version,

            cityCode,

            city: city.city,

            province: city.province,

            hub: city.hub,

            zone: city.zone,

            courier: city.courier || "CMI",

            service: city.service || "Regular",

            rateMode: rate.mode,

            weightTier: rate.tier,

            actualWeight: weight.actual,

            volumetricWeight: weight.volumetric,

            chargeableWeight: weight.chargeable,

            shippingCost: subtotal,

            shippingLabel: this.formatCurrency(subtotal),

            eta: city.eta,

            estimatedDays: city.estimated_days,

            grab: city.grab,

            freeShipping: city.free_shipping,

            dispatch: this.getDispatchStatus(),

            calculatedAt: new Date().toISOString()

        };

        shipping.summary = this.buildSummary(

            shipping

        );

        return shipping;

    }

    /* =====================================================
       SUMMARY
    ===================================================== */

    buildSummary(result) {

        return {

            destination:

                result.city +

                ", " +

                result.province,

            shipping:

                result.shippingLabel,

            eta:

                result.eta,

            weight:

                this.formatWeight(

                    result.chargeableWeight

                ),

            dispatch:

                result.dispatch

        };

    }

    /* =====================================================
       FREE SHIPPING
    ===================================================== */

    isFreeShipping(result) {

        if (!result) {

            return false;

        }

        return result.freeShipping === true;

    }

    /* =====================================================
       EXPRESS
    ===================================================== */

    isGrabExpress(result) {

        if (!result) {

            return false;

        }

        return result.grab === true;

    }

    /* =====================================================
       ETA LABEL
    ===================================================== */

    getEtaLabel(result) {

        if (!result) {

            return "-";

        }

        return result.eta +

            " hari";

    }

    /* =====================================================
       PRICE ONLY
    ===================================================== */

    getShippingCost(options) {

        const result =

            this.calculateShipping(options);

        if (!result.success) {

            return null;

        }

        return result.shippingCost;

    }

    /* =====================================================
       PRICE LABEL
    ===================================================== */

    getShippingLabel(options) {

        const result =

            this.calculateShipping(options);

        if (!result.success) {

            return "-";

        }

        return result.shippingLabel;

    }

    /* =====================================================
       QUICK ESTIMATION
    ===================================================== */

    estimate(cityCode, weight) {

        return this.calculateShipping({

            cityCode,

            actualWeight: weight

        });

    }
    /* =====================================================
       DOM RENDERER
    ===================================================== */

    createElement(tag, className = "", attributes = {}) {

        const element = document.createElement(tag);

        if (className) {

            element.className = className;

        }

        Object.entries(attributes).forEach(([key, value]) => {

            if (value !== undefined && value !== null) {

                element.setAttribute(key, value);

            }

        });

        return element;

    }

    /* =====================================================
       CLEAR ELEMENT
    ===================================================== */

    clearElement(element) {

        if (!element) return;

        while (element.firstChild) {

            element.removeChild(element.firstChild);

        }

    }

    /* =====================================================
       CREATE CITY ITEM
    ===================================================== */

    createCityItem(city, keyword = "") {

        const item = this.createElement(

            "div",

            "cmi-autocomplete-item",

            {

                "data-code": city.code,

                "role": "option"

            }

        );

        const title = this.createElement(

            "div",

            "cmi-autocomplete-title"

        );

        title.innerHTML = this.highlight(

            city.city,

            keyword

        );

        const subtitle = this.createElement(

            "div",

            "cmi-autocomplete-subtitle"

        );

        subtitle.textContent = city.province;

        item.appendChild(title);

        item.appendChild(subtitle);

        return item;

    }

    /* =====================================================
       EMPTY STATE
    ===================================================== */

    createEmptyState() {

        const empty = this.createElement(

            "div",

            "cmi-autocomplete-empty"

        );

        empty.textContent =

            "Kota tidak ditemukan";

        return empty;

    }

    /* =====================================================
       RENDER AUTOCOMPLETE
    ===================================================== */

    renderAutocomplete(container, keyword = "") {

        if (!container) {

            return;

        }

        this.clearElement(container);

        const list = this.search(keyword);

        if (!list.length) {

            container.appendChild(

                this.createEmptyState()

            );

            return;

        }

        const fragment = document.createDocumentFragment();

        list.forEach(city => {

            fragment.appendChild(

                this.createCityItem(

                    city,

                    keyword

                )

            );

        });

        container.appendChild(fragment);

    }

    /* =====================================================
       SHOW DROPDOWN
    ===================================================== */

    showDropdown(container) {

        if (!container) return;

        container.hidden = false;

        container.classList.add(

            "is-open"

        );

    }

    /* =====================================================
       HIDE DROPDOWN
    ===================================================== */

    hideDropdown(container) {

        if (!container) return;

        container.hidden = true;

        container.classList.remove(

            "is-open"

        );

    }

    /* =====================================================
       TOGGLE DROPDOWN
    ===================================================== */

    toggleDropdown(container, state) {

        if (state) {

            this.showDropdown(container);

        } else {

            this.hideDropdown(container);

        }

    }

    /* =====================================================
       RENDER SHIPPING RESULT
    ===================================================== */

    renderShippingResult(container, result) {

        if (!container) return;

        this.clearElement(container);

        if (!result || !result.success) {

            const error = this.createElement(

                "div",

                "cmi-shipping-error"

            );

            error.textContent =

                result?.message ||

                "Perhitungan gagal.";

            container.appendChild(error);

            return;

        }

        const wrapper = this.createElement(

            "div",

            "cmi-shipping-card"

        );

        const destination = this.createElement(

            "div",

            "cmi-destination"

        );

        destination.textContent =

            result.summary.destination;

        const price = this.createElement(

            "div",

            "cmi-price"

        );

        price.textContent =

            result.shippingLabel;

        const eta = this.createElement(

            "div",

            "cmi-eta"

        );

        eta.textContent =

            "Estimasi " +

            result.eta +

            " hari";

        wrapper.appendChild(destination);

        wrapper.appendChild(price);

        wrapper.appendChild(eta);

        container.appendChild(wrapper);

    }
    /* =====================================================
       AUTOCOMPLETE CONTROLLER
    ===================================================== */

    bindAutocomplete({

        input,

        dropdown,

        onSelect = null

    } = {}) {

        if (typeof input === "string") {

            input = document.querySelector(input);

        }

        if (typeof dropdown === "string") {

            dropdown = document.querySelector(dropdown);

        }

        if (!input || !dropdown) {

            return;

        }

        this.dom.input = input;
        this.dom.dropdown = dropdown;

        let activeIndex = -1;

        const render = () => {

            const keyword = input.value.trim();

            this.renderAutocomplete(

                dropdown,

                keyword

            );

            this.showDropdown(dropdown);

            activeIndex = -1;

        };

        const debouncedRender = this.debounce(render);

        input.addEventListener(

            "input",

            debouncedRender

        );

        this.eventPool.push({

            element: input,

            event: "input",

            handler: debouncedRender

        });

        input.addEventListener(

            "focus",

            render

        );

        this.eventPool.push({

            element: input,

            event: "focus",

            handler: render

        });

        input.addEventListener(

            "keydown",

            (e)=>{

                const items = dropdown.querySelectorAll(

                    ".cmi-autocomplete-item"

                );

                if (!items.length) {

                    return;

                }

                switch(e.key){

                    case "ArrowDown":

                        e.preventDefault();

                        activeIndex++;

                        break;

                    case "ArrowUp":

                        e.preventDefault();

                        activeIndex--;

                        break;

                    case "Escape":

                        this.hideDropdown(dropdown);

                        return;

                    case "Enter":

                        e.preventDefault();

                        if(activeIndex>=0){

                            items[activeIndex].click();

                        }

                        return;

                }

                if(activeIndex<0){

                    activeIndex=0;

                }

                if(activeIndex>=items.length){

                    activeIndex=items.length-1;

                }

                items.forEach(item=>{

                    item.classList.remove(

                        "is-active"

                    );

                });

                items[activeIndex]

                    .classList.add(

                        "is-active"

                    );

                items[activeIndex]

                    .scrollIntoView({

                        block:"nearest"

                    });

            }

        );

        dropdown.addEventListener(

            "click",

            (e)=>{

                const item = e.target.closest(

                    ".cmi-autocomplete-item"

                );

                if(!item){

                    return;

                }

                const code = item.dataset.code;

                const city = this.getCity(code);

                this.saveCity(code);

                input.value = city.city;

                this.hideDropdown(dropdown);

                if(typeof onSelect==="function"){

                    onSelect(city);

                }

            }

        );

        document.addEventListener(

            "click",

            (e)=>{

                if(

                    input.contains(e.target)

                    ||

                    dropdown.contains(e.target)

                ){

                    return;

                }

                this.hideDropdown(

                    dropdown

                );

            }

        );

    }

    /* =====================================================
       DESTROY EVENTS
    ===================================================== */

    destroy(){

        this.eventPool.forEach(event=>{

            event.element.removeEventListener(

                event.event,

                event.handler

            );

        });

        this.eventPool=[];

        this.searchCache.clear();

        this.dom={};

    }
    /* =====================================================
       GEOLOCATION MODULE
    ===================================================== */

    async getCurrentPosition(options = {}) {

        const settings = {

            enableHighAccuracy: true,

            timeout: 10000,

            maximumAge: 300000,

            ...options

        };

        return new Promise((resolve, reject) => {

            if (!navigator.geolocation) {

                reject(new Error("Browser tidak mendukung Geolocation"));

                return;

            }

            navigator.geolocation.getCurrentPosition(

                position => {

                    resolve({

                        latitude: position.coords.latitude,

                        longitude: position.coords.longitude,

                        accuracy: position.coords.accuracy

                    });

                },

                error => {

                    reject(error);

                },

                settings

            );

        });

    }

    /* =====================================================
       REVERSE GEOCODING
    ===================================================== */

    async reverseGeocode(latitude, longitude) {

        const url =
            "https://nominatim.openstreetmap.org/reverse?format=jsonv2"
            + "&lat=" + encodeURIComponent(latitude)
            + "&lon=" + encodeURIComponent(longitude);

        const response = await fetch(url, {

            headers: {

                "Accept": "application/json"

            }

        });

        if (!response.ok) {

            throw new Error("Reverse geocoding gagal.");

        }

        return await response.json();

    }

    /* =====================================================
       EXTRACT CITY
    ===================================================== */

    extractCityName(address = {}) {

        return (

            address.city ||

            address.city_district ||

            address.town ||

            address.county ||

            address.municipality ||

            address.state_district ||

            null

        );

    }

    /* =====================================================
       DETECT CITY
    ===================================================== */

    async detectCity() {

        try {

            const location =

                await this.getCurrentPosition();

            const result =

                await this.reverseGeocode(

                    location.latitude,

                    location.longitude

                );

            const cityName =

                this.extractCityName(

                    result.address

                );

            if (!cityName) {

                return null;

            }

            const city =

                this.getCityByName(

                    cityName

                );

            if (!city) {

                return null;

            }

            return city;

        }

        catch (error) {

            this.log(error);

            return null;

        }

    }

    /* =====================================================
       AUTO SELECT CITY
    ===================================================== */

    async autoSelectCity() {

        const city =

            await this.detectCity();

        if (!city) {

            return null;

        }

        const code =

            this.cityIndex.find(

                item => item.city === city.city

            )?.code;

        if (code) {

            this.saveCity(code);

        }

        return city;

    }

    /* =====================================================
       LOAD SAVED CITY
    ===================================================== */

    getSelectedCity() {

        const code =

            this.loadCityCode();

        if (!code) {

            return null;

        }

        return this.getCity(code);

    }

    /* =====================================================
       SET SELECTED CITY
    ===================================================== */

    setSelectedCity(code) {

        if (!this.hasCity(code)) {

            return false;

        }

        this.saveCity(code);

        return true;

    }

    /* =====================================================
       CLEAR SELECTED CITY
    ===================================================== */

    clearSelectedCity() {

        this.clearCity();

    }

    /* =====================================================
       INIT GEOLOCATION
    ===================================================== */

    async initGeolocation() {

        const saved =

            this.getSelectedCity();

        if (saved) {

            return saved;

        }

        return await this.autoSelectCity();

    }

    /* =====================================================
       LOCATION STATUS
    ===================================================== */

    async getLocationStatus() {

        try {

            const city =

                await this.initGeolocation();

            return {

                success: !!city,

                city

            };

        }

        catch (e) {

            return {

                success: false,

                city: null

            };

        }

    }
    /* =====================================================
       SHOPIFY PRODUCT
    ===================================================== */

    bindProduct(options = {}) {

        this.product = {

            weight: Number(options.weight || 0),

            length: Number(options.length || 0),

            width: Number(options.width || 0),

            height: Number(options.height || 0),

            cityInput: options.cityInput || null,

            dropdown: options.dropdown || null,

            resultContainer: options.resultContainer || null,

            calculateButton: options.calculateButton || null

        };

        return this.product;

    }

    /* =====================================================
       UPDATE PRODUCT
    ===================================================== */

    updateProduct(data = {}) {

        if (!this.product) {

            this.product = {};

        }

        Object.assign(this.product, data);

    }

    /* =====================================================
       GET PRODUCT
    ===================================================== */

    getProduct() {

        return this.product || null;

    }

    /* =====================================================
       CALCULATE FROM PRODUCT
    ===================================================== */

    calculateFromProduct(cityCode = null) {

        if (!this.product) {

            return {

                success: false,

                message: "Product belum di-bind."

            };

        }

        if (!cityCode) {

            cityCode = this.loadCityCode();

        }

        if (!cityCode) {

            return {

                success: false,

                message: "Kota belum dipilih."

            };

        }

        return this.calculateShipping({

            cityCode,

            actualWeight: this.product.weight,

            length: this.product.length,

            width: this.product.width,

            height: this.product.height

        });

    }

    /* =====================================================
       UPDATE SHIPPING WIDGET
    ===================================================== */

    updateShippingWidget(cityCode = null) {

        if (!this.product) {

            return;

        }

        const result =

            this.calculateFromProduct(

                cityCode

            );

        if (

            this.product.resultContainer

        ) {

            const container =

                typeof this.product.resultContainer === "string"

                ?

                document.querySelector(

                    this.product.resultContainer

                )

                :

                this.product.resultContainer;

            if (container) {

                this.renderShippingResult(

                    container,

                    result

                );

            }

        }

        return result;

    }

    /* =====================================================
       AUTO BIND CALCULATE BUTTON
    ===================================================== */

    bindCalculateButton() {

        if (

            !this.product ||

            !this.product.calculateButton

        ) {

            return;

        }

        const button =

            typeof this.product.calculateButton === "string"

            ?

            document.querySelector(

                this.product.calculateButton

            )

            :

            this.product.calculateButton;

        if (!button) {

            return;

        }

        const handler = () => {

            this.updateShippingWidget();

        };

        button.addEventListener(

            "click",

            handler

        );

        this.eventPool.push({

            element: button,

            event: "click",

            handler

        });

    }

    /* =====================================================
       AUTO UPDATE AFTER CITY SELECT
    ===================================================== */

    refreshAfterCitySelected(cityCode) {

        this.saveCity(cityCode);

        return this.updateShippingWidget(

            cityCode

        );

    }

    /* =====================================================
       RESET PRODUCT
    ===================================================== */

    resetProduct() {

        this.product = null;

    }

    /* =====================================================
       PRODUCT READY
    ===================================================== */

    isProductReady() {

        return !!this.product;

    }
    /* =====================================================
       VERSION
    ===================================================== */

    getVersion() {

        return this.version;

    }

    /* =====================================================
       READY
    ===================================================== */

    isReady() {

        return this.ready === true;

    }

    /* =====================================================
       RESET
    ===================================================== */

    reset() {

        this.searchCache.clear();

        this.clearCity();

        this.dom = {};

        this.product = null;

        return this;

    }

    /* =====================================================
       PUBLIC INFO
    ===================================================== */

    info() {

        return {

            version: this.version,

            ready: this.ready,

            cities: this.cityIndex.length,

            cache: this.searchCache.size,

            storageKey: this.config.storageKey

        };

    }

    /* =====================================================
       NO CONFLICT
    ===================================================== */

    static noConflict() {

        const current = window.ShippingEngine;

        window.ShippingEngine = undefined;

        return current;

    }

}

/* ==========================================================
   EXPORT
========================================================== */

window.ShippingEngine = ShippingEngine;

/* ==========================================================
   AUTO INIT
========================================================== */

window.createShippingEngine = async function(options = {}) {

    const engine = new ShippingEngine(options);

    await engine.init();

    return engine;

};

/* ==========================================================
   DOM READY HELPER
========================================================== */

window.onShippingReady = function(callback){

    if(document.readyState==="loading"){

        document.addEventListener(

            "DOMContentLoaded",

            callback

        );

    }else{

        callback();

    }

};

})(window,document);